/**
 * The /redeem/:code? page.
 *
 * State machine (full diagram in ../types.ts → RedeemStep):
 *
 *   entry/loading → preview → (auth) → claiming → receipt
 *                     ↓
 *                   error states (not_found / expired / already_claimed /
 *                                 email_already_redeemed / network_error /
 *                                 invalid_format)
 *
 * URL forms:
 *   - /redeem            → step = entry  (manual code input)
 *   - /redeem/<code>     → run preview on mount (or show invalid_format
 *                          immediately if the checksum doesn't match)
 *
 * OAuth round-trip handling:
 *   When the user picks Microsoft sign-in from <ClaimAuthStep>, the
 *   browser may navigate to login.microsoftonline.com and back, losing
 *   in-memory state. ClaimAuthStep stashes the pending code in
 *   sessionStorage on mouse-down. On mount we read that key — if it
 *   matches our current code AND the user is already authed (the OAuth
 *   handler wrote the JWT before bouncing back), we auto-fire the claim.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import useAuth from '../../auth/hooks/useAuth';
import { CouponApiError, claimCode, previewCode } from '../services/couponApi';
import { CouponPreview, ClaimReceipt, RedeemStep } from '../types';
import { validateCodeChecksum } from '../utils/checksum';
import CodeEntryStep from './CodeEntryStep';
import GiftPreviewCard from './GiftPreviewCard';
import ClaimAuthStep, { PENDING_CLAIM_CODE_KEY } from './ClaimAuthStep';
import ReceiptScreen from './ReceiptScreen';

const RedeemLanding: React.FC = () => {
  const { code: codeFromUrl } = useParams<{ code?: string }>();
  const { isAuthenticated } = useAuth();

  const [step, setStep] = useState<RedeemStep>('entry');
  const [code, setCode] = useState<string | null>(null);
  const [preview, setPreview] = useState<CouponPreview | null>(null);
  const [receipt, setReceipt] = useState<ClaimReceipt | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** Backend's exact error code, when we have one — used by the test
   *  suite + by future copy refinements. */
  const [errorCode, setErrorCode] = useState<string | null>(null);

  /** Guards against double-firing the claim in StrictMode dev / re-renders. */
  const claimInFlight = useRef(false);

  /**
   * Map a CouponApiError to a step + user-facing message. Pure — no
   * setState — so we can call it from anywhere in the flow.
   */
  const stepFromError = (err: CouponApiError): { step: RedeemStep; message: string } => {
    switch (err.code) {
      case 'NOT_FOUND':
        return { step: 'not_found', message: "We don't recognize that code." };
      case 'EXPIRED':
        return { step: 'expired', message: 'This code has expired.' };
      case 'ALREADY_CLAIMED':
        return {
          step: 'already_claimed_by_other',
          message: 'This code has already been claimed by another account.',
        };
      case 'EMAIL_ALREADY_REDEEMED':
        return {
          step: 'email_already_redeemed',
          message: 'This account has already redeemed a convention code.',
        };
      case 'NETWORK':
        return {
          step: 'network_error',
          message: "We couldn't reach the server. Check your connection and retry.",
        };
      default:
        return {
          step: 'network_error',
          message: err.message || 'Something went wrong. Please try again.',
        };
    }
  };

  /**
   * Kick off the preview lookup for a given code. Wrapped in
   * useCallback because both the URL-driven effect and the manual-entry
   * submit handler call it.
   */
  const runPreview = useCallback(async (rawCode: string) => {
    const check = validateCodeChecksum(rawCode);
    if (!check.valid) {
      setCode(rawCode);
      setStep('invalid_format');
      setErrorMessage("This doesn't look like a valid code.");
      setErrorCode('INVALID_FORMAT');
      return;
    }
    const normalized = check.normalized!;
    setCode(normalized);
    setStep('loading');
    setErrorMessage(null);
    setErrorCode(null);
    try {
      const p = await previewCode(normalized);
      setPreview(p);
      setStep('preview');
    } catch (err) {
      const apiErr = err as CouponApiError;
      const next = stepFromError(apiErr);
      setStep(next.step);
      setErrorMessage(next.message);
      setErrorCode(apiErr.code);
    }
  }, []);

  /**
   * Fire the claim. Idempotent calls also land here — the backend
   * returns the same shape with `idempotent:true`. We accept a code
   * argument so the post-OAuth pickup can call it without waiting for
   * the React-state version to settle.
   */
  const runClaim = useCallback(async (claimableCode: string) => {
    if (claimInFlight.current) return;
    claimInFlight.current = true;
    setStep('claiming');
    try {
      const r = await claimCode(claimableCode);
      setReceipt(r);
      setStep('receipt');
      // Clear the post-OAuth pickup once we've handed it back. Safe to
      // call even when the key isn't present.
      try {
        window.sessionStorage.removeItem(PENDING_CLAIM_CODE_KEY);
      } catch {
        /* storage disabled — ignore */
      }
    } catch (err) {
      const apiErr = err as CouponApiError;
      const next = stepFromError(apiErr);
      setStep(next.step);
      setErrorMessage(next.message);
      setErrorCode(apiErr.code);
    } finally {
      claimInFlight.current = false;
    }
  }, []);

  // -------------------------------------------------------------- //
  // Mount: pick up the URL code AND any post-OAuth pending code.    //
  // -------------------------------------------------------------- //
  useEffect(() => {
    if (codeFromUrl) {
      runPreview(codeFromUrl);
    }
    // No code in URL — leave step at 'entry'. The user types one in.
  }, [codeFromUrl, runPreview]);

  /**
   * After auth (in-page sign-in OR post-OAuth-bounce): if we have a
   * code in preview AND the user is now authenticated, auto-fire the
   * claim. The user shouldn't have to click "Claim" twice.
   */
  useEffect(() => {
    if (!isAuthenticated) return;
    // Recover from a Microsoft OAuth bounce: ClaimAuthStep stashed the
    // code we were trying to claim before the redirect.
    let pendingFromStorage: string | null = null;
    try {
      pendingFromStorage = window.sessionStorage.getItem(PENDING_CLAIM_CODE_KEY);
    } catch {
      pendingFromStorage = null;
    }
    // Prefer the in-memory code (covers in-page sign-in case); fall
    // back to sessionStorage (covers OAuth bounce case).
    const claimable = code || pendingFromStorage;
    if (!claimable) return;
    // Only fire when we're in a state that's waiting for auth. If we're
    // already at 'receipt' or actively 'claiming', leave it alone.
    if (step === 'auth' || step === 'preview') {
      runClaim(claimable);
    } else if (pendingFromStorage && !code && step === 'entry') {
      // Post-bounce landing without a URL code — run preview first so
      // the user sees what they're claiming, then the auth-listening
      // path above will kick claim.
      runPreview(pendingFromStorage);
    }
  }, [isAuthenticated, code, step, runClaim, runPreview]);

  // -------------------------------------------------------------- //
  // Handlers from child components.                                 //
  // -------------------------------------------------------------- //

  const handleClaimClick = () => {
    // Defensive guards: the `!code` early-out and the `isAuthenticated`
    // branch are structurally unreachable from the UI today — the button
    // mounts only inside <GiftPreviewCard> which only renders when
    // step === 'preview' AND `code` is set, and the auth-listening
    // effect above ALREADY fires runClaim once
    // (isAuthenticated && step === 'preview'). We keep both for the
    // (currently impossible) race where the effect hasn't flushed yet.
    /* v8 ignore start */
    if (!code) return;
    if (isAuthenticated) {
      runClaim(code);
    } else {
      /* v8 ignore stop */
      setStep('auth');
    }
  };

  const handleAuthenticated = () => {
    // The useEffect above will pick this up and fire runClaim.
    // We just need to nudge step so the effect's gate (step==='auth')
    // is satisfied even if there's a brief race. The `code` falsy branch
    // is structurally unreachable: ClaimAuthStep only mounts when we have
    // a code in state, and the auth step's onAuthenticated callback is
    // only wired from that mount.
    /* v8 ignore start */
    if (code) runClaim(code);
    /* v8 ignore stop */
  };

  const handleReset = () => {
    setStep('entry');
    setCode(null);
    setPreview(null);
    setReceipt(null);
    setErrorMessage(null);
    setErrorCode(null);
  };

  // -------------------------------------------------------------- //
  // Rendering.                                                      //
  // -------------------------------------------------------------- //

  if (step === 'entry') {
    return (
      <CodeEntryStep
        onSubmit={(c) => runPreview(c)}
        initialValue={codeFromUrl || ''}
      />
    );
  }

  if (step === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          py: 6,
        }}
        data-testid="redeem-loading"
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Looking up your code…
        </Typography>
      </Box>
    );
  }

  if (step === 'preview' && preview) {
    return (
      <GiftPreviewCard
        preview={preview}
        isAuthenticated={isAuthenticated}
        onClaim={handleClaimClick}
        claiming={false}
      />
    );
  }

  if (step === 'auth' && code) {
    return (
      <ClaimAuthStep
        pendingCode={code}
        onAuthenticated={handleAuthenticated}
      />
    );
  }

  if (step === 'claiming') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          py: 6,
        }}
        data-testid="redeem-claiming"
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Applying your reward…
        </Typography>
      </Box>
    );
  }

  if (step === 'receipt' && receipt) {
    return <ReceiptScreen receipt={receipt} />;
  }

  // All error states share the same chrome — only copy differs.
  return (
    <Paper
      elevation={3}
      data-testid="redeem-error"
      data-error-code={errorCode || undefined}
      data-step={step}
      sx={{ p: { xs: 3, sm: 4 } }}
    >
      <Stack spacing={2}>
        <Typography variant="h5" component="h1">
          {step === 'email_already_redeemed'
            ? 'Already redeemed'
            : step === 'already_claimed_by_other'
              ? 'Code already used'
              : step === 'expired'
                ? 'Code expired'
                : step === 'not_found'
                  ? "Code not found"
                  : step === 'invalid_format'
                    ? "That doesn't look right"
                    : "Something went wrong"}
        </Typography>
        <Alert severity={step === 'network_error' ? 'warning' : 'error'}>
          {errorMessage || 'Please try again.'}
        </Alert>
        <Stack direction="row" spacing={2}>
          {step === 'network_error' && code && (
            <Button
              variant="contained"
              onClick={() => runPreview(code)}
              data-testid="redeem-retry"
            >
              Retry
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={handleReset}
            data-testid="redeem-try-another"
          >
            Try a different code
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default RedeemLanding;
